import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, ArrowRight, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import { useT } from "../lib/i18n";
import { api } from "../lib/api";

export default function Apply() {
    const { t } = useT();
    const navigate = useNavigate();
    const fileRef = useRef(null);

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phone, setPhone] = useState("");
    const [photo, setPhoto] = useState(null);
    const [preview, setPreview] = useState("");
    const [ageOk, setAgeOk] = useState(false);
    const [smsOk, setSmsOk] = useState(false);
    const [busy, setBusy] = useState(false);

    const onPickFile = (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please choose an image file.");
            return;
        }
        if (file.size > 6 * 1024 * 1024) {
            toast.error("Image is too large (max 6 MB).");
            return;
        }
        setPhoto(file);
        const url = URL.createObjectURL(file);
        setPreview(url);
    };

    const canSubmit =
        firstName.trim().length >= 1 &&
        lastName.trim().length >= 1 &&
        phone.trim().length >= 4 &&
        photo &&
        ageOk &&
        smsOk &&
        !busy;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) {
            toast.error(t("join.error_required"));
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("first_name", firstName.trim());
            fd.append("last_name", lastName.trim());
            fd.append("phone", phone.trim());
            fd.append("age_confirmed", "true");
            fd.append("sms_consent", "true");
            fd.append("photo", photo);

            await api.post("/applications", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            navigate("/success", { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail || err.message || "Something went wrong.";
            toast.error(typeof msg === "string" ? msg : "Something went wrong.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-[100dvh] text-ink-primary relative" data-testid="apply-page">
            <Header />

            <main className="relative max-w-xl mx-auto px-6 pt-32 pb-24">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <div className="label-eyebrow">{t("join.eyebrow")}</div>
                    <h1 className="font-serif font-medium text-4xl md:text-5xl mt-4 leading-tight">
                        {t("join.title")}
                    </h1>
                    <p className="text-ink-secondary mt-4 leading-relaxed">{t("join.subtitle")}</p>
                </motion.div>

                <form className="mt-12 space-y-8" onSubmit={onSubmit} data-testid="apply-form">
                    {/* Photo */}
                    <div>
                        <div className="label-eyebrow mb-3">{t("join.label_photo")}</div>
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            data-testid="photo-input"
                            onChange={(e) => onPickFile(e.target.files?.[0])}
                        />
                        {!preview ? (
                            <button
                                type="button"
                                onClick={() => fileRef.current?.click()}
                                data-testid="photo-picker"
                                className="w-full aspect-[4/3] rounded-sm border border-dashed border-gold/50 hover:border-gold/80 transition-colors flex flex-col items-center justify-center gap-3 bg-bg-secondary/40 hover:bg-bg-secondary/70"
                            >
                                <Camera size={28} className="text-gold" strokeWidth={1.2} />
                                <span className="text-ink-secondary text-sm">{t("join.photo_drop")}</span>
                            </button>
                        ) : (
                            <div className="relative aspect-[4/3] rounded-sm overflow-hidden border border-gold/40" data-testid="photo-preview">
                                <img src={preview} alt="preview" className="w-full h-full object-cover" />
                                <button
                                    type="button"
                                    onClick={() => { setPhoto(null); setPreview(""); if (fileRef.current) fileRef.current.value = ""; }}
                                    data-testid="photo-remove"
                                    className="absolute top-3 right-3 w-9 h-9 rounded-full glass-strong flex items-center justify-center text-ink-primary hover:text-gold"
                                >
                                    <X size={16} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => fileRef.current?.click()}
                                    className="absolute bottom-3 right-3 px-3 py-1.5 rounded-sm glass-strong text-xs tracking-[0.2em] uppercase text-gold hover:bg-gold/10"
                                >{t("join.photo_replace")}</button>
                            </div>
                        )}
                        <p className="text-ink-muted text-xs mt-3 leading-relaxed">{t("join.photo_help")}</p>
                    </div>

                    {/* Name fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                            <label className="label-eyebrow block mb-2">{t("join.label_first_name")}</label>
                            <input
                                data-testid="input-first-name"
                                className="input-line"
                                placeholder={t("join.ph_first_name")}
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                maxLength={40}
                                autoComplete="given-name"
                            />
                        </div>
                        <div>
                            <label className="label-eyebrow block mb-2">{t("join.label_last_name")}</label>
                            <input
                                data-testid="input-last-name"
                                className="input-line"
                                placeholder={t("join.ph_last_name")}
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                maxLength={40}
                                autoComplete="family-name"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="label-eyebrow block mb-2">{t("join.label_phone")}</label>
                        <input
                            data-testid="input-phone"
                            className="input-line"
                            placeholder={t("join.ph_phone")}
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            inputMode="tel"
                            autoComplete="tel"
                        />
                    </div>

                    {/* Age + SMS consent */}
                    <div className="pt-2 space-y-4">
                        <label className="flex items-start gap-3 text-sm text-ink-secondary cursor-pointer">
                            <input
                                type="checkbox"
                                data-testid="check-age"
                                checked={ageOk}
                                onChange={(e) => setAgeOk(e.target.checked)}
                                className="mt-1 w-4 h-4 accent-[#D4AF37]"
                            />
                            <span>{t("join.age_label")}</span>
                        </label>
                        <label className="flex items-start gap-3 text-sm text-ink-secondary cursor-pointer">
                            <input
                                type="checkbox"
                                data-testid="check-sms"
                                checked={smsOk}
                                onChange={(e) => setSmsOk(e.target.checked)}
                                className="mt-1 w-4 h-4 accent-[#D4AF37]"
                            />
                            <span>{t("join.sms_label")}</span>
                        </label>
                    </div>

                    <button
                        type="submit"
                        disabled={!canSubmit}
                        data-testid="submit-apply"
                        className="w-full btn-gold inline-flex items-center justify-center gap-3 px-10 py-4 rounded-sm font-medium tracking-wide disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {busy ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                {t("join.submitting")}
                            </>
                        ) : (
                            <>
                                {t("join.submit")}
                                <ArrowRight size={18} />
                            </>
                        )}
                    </button>
                </form>
            </main>
        </div>
    );
}
